import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { MessageSquare, Pin, PinOff, Plus, Search, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    AiConversation,
    deleteConversation,
    MAX_CONVERSATIONS,
    togglePinConversation,
} from "../../utils/aiConversations";
import "./AiConversationList.css";

interface Props {
    conversations: AiConversation[];
    activeId: string | null;
    userId: number | undefined;
    onSelect: (conv: AiConversation) => void;
    onNew: () => void;
    onClose: () => void;
    onDelete: (id: string) => void;
    setConversations: Dispatch<SetStateAction<AiConversation[]>>;
}

function formatRelativeDate(iso: string, now: string, language: string): string {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const minutes = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);

    if (minutes < 1) return now;
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    const locale = language === "us" ? "en-US" : language === "es" ? "es-ES" : "pt-BR";
    return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
}

const AiConversationList = ({
    conversations,
    activeId,
    userId,
    onSelect,
    onNew,
    onClose,
    onDelete,
    setConversations,
}: Props) => {
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState("");

    const filteredConversations = useMemo(() => {
        const query = search.trim().toLocaleLowerCase(i18n.language);
        if (!query) return conversations;
        return conversations.filter(conversation => {
            const botName = conversation.ai === "bochecha" ? "Bochecha AI" : "Paçoca AI";
            return `${conversation.title} ${botName}`.toLocaleLowerCase(i18n.language).includes(query);
        });
    }, [conversations, i18n.language, search]);

    const handleDelete = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        deleteConversation(userId, id);
        setConversations(prev => prev.filter(conversation => conversation.id !== id));
        onDelete(id);
    };

    const handleTogglePin = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        setConversations(togglePinConversation(userId, id));
    };

    return (
        <div className="ai-conv-list">
            <div className="ai-conv-list-header">
                <div className="ai-conv-list-heading">
                    <button
                        className="ai-conv-icon-btn"
                        onClick={onClose}
                        title={t("generics.close", { defaultValue: "Fechar" })}
                        type="button"
                    >
                        <X size={17} />
                    </button>
                    <span className="ai-conv-list-title">{t("pacocaAI.conversations")}</span>
                    <span className="ai-conv-count">{conversations.length}/{MAX_CONVERSATIONS}</span>
                </div>

                <button
                    className="ai-conv-icon-btn ai-conv-new-btn"
                    onClick={onNew}
                    title={t("pacocaAI.newConversation")}
                    type="button"
                >
                    <Plus size={17} />
                </button>
            </div>

            {conversations.length > 0 && (
                <div className="ai-conv-search-wrap">
                    <Search size={15} aria-hidden="true" />
                    <input
                        className="ai-conv-search"
                        type="search"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder={t("pacocaAI.searchConversations", { defaultValue: "Pesquisar conversas" })}
                        autoFocus
                    />
                </div>
            )}

            <div className="ai-conv-list-items">
                {filteredConversations.length === 0 && (
                    <p className="ai-conv-empty">
                        {search
                            ? t("pacocaAI.noSearchResults", { defaultValue: "Nenhuma conversa encontrada" })
                            : t("pacocaAI.noConversations")}
                    </p>
                )}

                {filteredConversations.map(conversation => {
                    const pinned = Boolean(conversation.is_pinned);
                    return (
                        <div
                            key={conversation.id}
                            className={`ai-conv-item ${conversation.id === activeId ? "ai-conv-item--active" : ""}`}
                        >
                            <button
                                type="button"
                                className="ai-conv-item-main"
                                onClick={() => onSelect(conversation)}
                                title={conversation.title}
                            >
                                {pinned
                                    ? <Pin size={14} className="ai-conv-item-icon ai-conv-item-icon--pinned" />
                                    : <MessageSquare size={14} className="ai-conv-item-icon" />}
                                <span className="ai-conv-item-copy">
                                    <span className="ai-conv-item-title">{conversation.title}</span>
                                    <span className="ai-conv-item-meta">
                                        {conversation.ai === "bochecha" ? "Bochecha AI" : "Paçoca AI"}
                                    </span>
                                </span>
                                <span className="ai-conv-item-date">
                                    {formatRelativeDate(conversation.updated_at, t("pacocaAI.now"), i18n.language)}
                                </span>
                            </button>

                            <div className="ai-conv-item-actions">
                                <button
                                    type="button"
                                    className={`ai-conv-item-action${pinned ? " ai-conv-item-action--pinned" : ""}`}
                                    onClick={event => handleTogglePin(event, conversation.id)}
                                    title={t(
                                        pinned ? "pacocaAI.unpinConversation" : "pacocaAI.pinConversation",
                                        { defaultValue: pinned ? "Desafixar conversa" : "Fixar conversa" },
                                    )}
                                >
                                    {pinned ? <PinOff size={14} /> : <Pin size={14} />}
                                </button>
                                <button
                                    type="button"
                                    className="ai-conv-item-action ai-conv-item-action--delete"
                                    onClick={event => handleDelete(event, conversation.id)}
                                    title={t("pacocaAI.deleteConversation")}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AiConversationList;
